import type { HookFetchPlugin } from 'hook-fetch';
import { ElMessage } from 'element-plus';
import hookFetch from 'hook-fetch';
import { getApiClientId, parseJsonPreserveLongIds } from '@/api/knowledge/helpers';
import { EMBED_BRIDGE_PATH } from '@/config';
import router from '@/routers';
import { resolveHttpErrorMessage } from '@/utils/httpErrorMessage';

interface BaseResponse {
  code: number;
  data: any;
  message: string;
  success: boolean;
  timestamp: number;
}

export interface RequestError extends Error {
  code?: number;
  toastShown?: boolean;
}

function createRequestError(message: string, code?: number, toastShown = false): RequestError {
  const error = new Error(message) as RequestError;
  error.code = code;
  error.toastShown = toastShown;
  return error;
}

// 不跟随前端域名，使用环境变量指定接口域名
const baseURL = import.meta.env.VITE_API_URL || '';
export const request = hookFetch.create<BaseResponse>({
  baseURL, // 使用上面定义的baseURL变量
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 添加超时设置
});

function jwtPlugin(): HookFetchPlugin<BaseResponse> {
  return {
    name: 'jwt',
    beforeRequest: async (config) => {
      if (import.meta.env.DEV && config.url) {
        const url = String(config.url);
        console.log('url', url);
        // request 已经配置了 baseURL（VITE_API_URL）。
        // 若这里再把 url 手动拼成绝对地址，会导致 baseURL + 绝对url 的重复拼接：
        // 例如：http://a:16039http://a:16039/auth/code
        // 因此：一旦发现是绝对地址，统一转换为相对路径交给 baseURL 处理。
        if (/^https?:\/\//.test(url)) {
          try {
            const u = new URL(url);
            config.url = `${u.pathname}${u.search}${u.hash}`;
          }
          catch {
            // fallback：尽量剥离域名
            config.url = url.replace(/^https?:\/\/[^/]+/, '');
          }
        }
      }

      // 延迟导入useUserStore，避免循环依赖
      const { useUserStore } = await import('@/stores');
      const userStore = useUserStore();
      // 注意：不要在生产环境使用 fallback token。
      // 使用 fallbackToken 会导致在“未登录/登录态异常”时依然携带一个有效 token，
      // 进而让本应失败的接口返回成功（表现为：接口报错/未授权，但页面仍提示成功）。
      const token = userStore.token;
      config.headers = new Headers(config.headers);
      if (token)
        config.headers.set('Authorization', `Bearer ${token}`);
      const clientId = getApiClientId();
      if (clientId)
        config.headers.set('clientId', clientId);

      return config;
    },
    afterResponse: async (context) => {
      try {
        if (context.response && context.response.ok) {
          const contentType = context.response.headers.get('content-type') || '';
          // SSE 流式接口（text/event-stream）不是 JSON，不能走 response.json()
          if (contentType.includes('text/event-stream')) {
            const textData = await context.response.text();
            context.result = {
              code: 200,
              data: textData,
              message: 'OK',
              success: true,
              timestamp: Date.now(),
            } as BaseResponse;
            return context;
          }

          const responseUrl = context.response?.url || '';
          const isKnowledgeApi = responseUrl.includes('/ai/knowledge/');

          // 解析 JSON（知识库接口的长整型 ID 保持字符串，避免精度丢失）
          let jsonData;
          try {
            if (isKnowledgeApi) {
              const text = await context.response.text();
              jsonData = parseJsonPreserveLongIds(text);
            }
            else {
              jsonData = await context.response.json();
            }
          }
          catch (jsonError) {
            console.error('JSON parsing error:', jsonError);
            throw jsonError;
          }

          const msg = jsonData?.message ?? jsonData?.msg ?? 'OK';

          // 成功判断：
          // 1) code=200/0 视为成功
          // 2) 若后端返回 success=false，则无论 code 如何都视为失败
          const isOk = (jsonData?.code === 200 || jsonData?.code === 0) && jsonData?.success !== false;

          if (isOk) {
            context.result = {
              ...jsonData,
              success: true,
              message: msg,
            } as BaseResponse;
            return context;
          }

          // 业务失败：401/403/404 等需要特殊处理，其它统一提示并 throw
          if (jsonData?.code === 404)
            throw createRequestError(msg || '资源不存在', 404);

          if (jsonData?.code === 403) {
            // 知识库模块在页面内展示无权限状态，避免整页跳转 403
            if (!isKnowledgeApi)
              router.replace({ name: '403' });
            ElMessage.error(msg || '权限不足');
            throw createRequestError(msg || '权限不足', 403, true);
          }

          if (jsonData?.code === 401) {
            const { useUserStore } = await import('@/stores');
            const userStore = useUserStore();
            const onEmbedBridge = router.currentRoute.value.path === EMBED_BRIDGE_PATH;
            // 先清理登录态（embed-bridge 内 logout 不会跳转）
            await userStore.logout();

            if (!onEmbedBridge) {
              // 再显式引导到登录页/弹窗
              if (typeof userStore.openLoginDialog === 'function') {
                userStore.openLoginDialog();
              }
              router.replace({ path: '/login' });
            }

            throw createRequestError(msg || '未授权', 401);
          }

          if (!isKnowledgeApi)
            ElMessage.error(msg || '请求失败');
          throw createRequestError(msg || '请求失败', jsonData?.code, !isKnowledgeApi);
        }

        // 非 2xx：优先解析后端返回的 message/msg 再提示（避免弹出 nginx HTML 错误页）
        const status = context.response?.status;
        const statusText = context.response?.statusText;

        let jsonMessage: string | undefined;
        let bodyText: string | undefined;
        try {
          // clone() 避免响应体被消费影响后续（部分环境可能需要）
          const cloned = context.response?.clone?.() || context.response;
          const contentType = cloned?.headers?.get?.('content-type') || '';

          if (cloned) {
            if (contentType.includes('application/json')) {
              const data: any = await cloned.json();
              jsonMessage = data?.message || data?.msg || data?.error;
            }
            else {
              bodyText = await cloned.text();
            }
          }
        }
        catch {
          // ignore parse errors
        }

        const errorMsg = resolveHttpErrorMessage({
          status,
          statusText,
          jsonMessage,
          bodyText,
          fallback: `请求失败（HTTP ${status || 'unknown'}）`,
        });

        // 统一提示一次（页面层不再重复提示）
        ElMessage.error(errorMsg);
        throw createRequestError(errorMsg, status, true);
      }
      catch (error) {
        console.error('After response error:', error);
        throw error;
      }
    },
    // hook-fetch 在 afterResponse 抛错后会走 normalizeError 并 resolve 错误对象；
    // 这里重新抛出，确保 await xxx.json() 能进入业务层 catch。
    onError: async (error) => {
      throw error;
    },
  };
}

request.use(jwtPlugin());

export const post = request.post;
export const get = request.get;
export const put = request.put;
export const del = request.delete;

export default request;
